import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './groupDialog.module.scss'
import { useMutation } from '@apollo/client'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import UserInput from '@/components/_custom/UserInput/userInput'
import { Daycare } from '@/types/Daycare'
import { Group } from '@/types/Group'
import { CREATE_GROUP, UPDATE_GROUP } from '@/modules/API/daycare/groupHandler'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface groupDialogProps {
    open: boolean
    handleClose: () => void
    daycare: Daycare
    create: boolean
    // CREATE
    addOneGroup?: (value: Group) => void
    // EDIT
    groupId?: number
    groupName?: string
    setGroupName?: (value: string) => void
    updateOneGroup?: (value: Group) => void
}

const GroupDialog: FunctionComponent<groupDialogProps> = ({
    open,
    handleClose,
    daycare,
    create,
    addOneGroup,
    groupId,
    groupName,
    setGroupName,
    updateOneGroup
}: groupDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [newGroupName, setNewGroupName] = useState<string>(groupName || '')

    const [createGroup] = useMutation(CREATE_GROUP, {
        variables: {
            daycare_id: daycare.id,
            name: newGroupName
        },
        onCompleted: ({ createGroup }) => {
            if (addOneGroup)
                addOneGroup({
                    id: createGroup,
                    daycareId: daycare.id,
                    name: newGroupName
                })

            setNewGroupName('')

            handleClose()

            showNotificationSuccess(t('groupPriorityAdded'))
        },
        onError: () => {
            showNotificationError(t('errorAddedGroupPriority'))
        }
    })

    const [updateGroup] = useMutation(UPDATE_GROUP, {
        variables: {
            group_id: Number(groupId) || 0,
            name: newGroupName,
            daycare_id: Number(daycare.id) || 0
        },
        onCompleted: () => {
            if (updateOneGroup && groupId)
                updateOneGroup({
                    id: groupId,
                    daycareId: daycare.id,
                    name: newGroupName
                })

            setNewGroupName('')

            if (setGroupName) setGroupName(newGroupName)

            handleClose()

            showNotificationSuccess(t('groupPriorityUpdated'))
        },
        onError: () => {
            showNotificationError(t('errorUpdatedGroupPriority'))
        }
    })

    const disabled: boolean = create ? newGroupName === '' : groupName === newGroupName

    return (
        <Dialog open={open} handleClose={handleClose} className={styles.Dialog}>
            <DialogHeader title={create ? t('addPriorityGroup') : t('updatePriorityGroup')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <div className={styles.Input}>
                        <UserInput
                            label={t('name')}
                            value={newGroupName}
                            type='text'
                            required
                            fullWidth
                            format={'pascal'}
                            className={styles.UserInput}
                            unallowedCharacters={'special'}
                            setValue={setNewGroupName}
                        />
                    </div>
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={create ? 'add' : 'update'}
                        onClick={create ? createGroup : updateGroup}
                        className={styles.Button}
                        disabled={disabled}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default GroupDialog
